/**
 * Template Out of Sync Banner
 * Shows count of outdated objects and allows bulk updating
 */

import React, { useState, useMemo } from 'react';
import { GlossaryBlueprint } from '@/types-games';
import { getOutOfSyncObjects } from '@/lib/utils/objectSync';
import { Button } from '@/components/ui/button';
import IconProvider from '@/components/ui/IconProvider';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useGlossaryDataContext } from '@/providers/ObjectsDataProvider';
import OutOfSyncObjectsList from './OutOfSyncObjectsList';
import { TemplateSyncWizard } from './TemplateSyncWizard';

interface TemplateOutOfSyncBannerProps {
  blueprint: GlossaryBlueprint;
}

export default function TemplateOutOfSyncBanner({ blueprint }: TemplateOutOfSyncBannerProps) {
  const { selectedGame } = useSimplifiedUserData();
  const { glossaryItems } = useGlossaryDataContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Find outdated objects from the context data (automatically updates when store changes)
  const outOfSyncObjects = useMemo(() => {
    return getOutOfSyncObjects(blueprint, glossaryItems);
  }, [blueprint, glossaryItems]);

  // Don't show banner if no objects are out of sync
  if (outOfSyncObjects.length === 0) {
    return null;
  }

  const handleUpdateAll = () => {
    if (!selectedGame?.id) return;
    setIsWizardOpen(true);
  };

  return (
    <div className="border border-warning/50 bg-warning/10 rounded-lg overflow-hidden mb-4">
      {/* Banner Header */}
      <div className="p-4 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <IconProvider 
            iconstring="fa-exclamation-triangle" 
            className="text-warning" 
            size={20}
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-warning-foreground">
                {outOfSyncObjects.length} Object{outOfSyncObjects.length !== 1 ? 's' : ''} Out of Date
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {outOfSyncObjects.length === 1 
                  ? 'This object is using an older version of this template and may have missing or outdated fields.'
                  : 'These objects are using an older version of this template and may have missing or outdated fields.'}
              </p>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="gap-2"
              >
                <IconProvider 
                  iconstring={isExpanded ? "fa-chevron-up" : "fa-chevron-down"} 
                  size={12}
                />
                {isExpanded ? 'Hide' : 'Show'} Details
              </Button>
              
              <Button
                onClick={handleUpdateAll}
                size="sm"
                className="gap-2"
              >
                <IconProvider iconstring="fa-sync" size={12} />
                Update All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <OutOfSyncObjectsList 
          outOfSyncObjects={outOfSyncObjects} 
          blueprint={blueprint} 
        />
      )}

      {/* Sync Wizard */}
      <TemplateSyncWizard
        open={isWizardOpen}
        onOpenChange={setIsWizardOpen}
        blueprint={blueprint}
        outOfSyncObjects={outOfSyncObjects}
      />
    </div>
  );
}
